import React, { useEffect, useState, useCallback } from "react";
import { Modal, Badge, Table, OverlayTrigger, Tooltip as BsTooltip } from "react-bootstrap";
import { Btn } from "../../../../shared/Btn";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { ReduxState } from "../../../../redux";
import { setSelectedDocumentId } from "src/redux/reducers/TransactionManager";
import {
  getDuplicateBankTransactionsForProject,
  DuplicateBankTransactionsResponse,
  DuplicateBankTransactionGroup,
  DuplicateBankTransactionMember,
} from "../../../../docuclipper/api";

// One-time, post-upload duplicate-detection modal. Replaces the older
// persistent chip + module-level cache + 9-site invalidation plumbing.
//
// Trigger contract (owned by callers):
//   - Open this modal once, immediately after newly-uploaded documents
//     finish extraction in the project. The caller passes `isOpen=true`
//     and a stable `triggerKey` (e.g. the comma-joined newDocuments
//     ids) so we can distinguish a fresh trigger from a re-render.
//   - We fetch on open. If the response has zero groups, we self-close
//     without ever rendering the modal frame (no empty-state flash).
//   - Caller is responsible for clearing whatever URL/state caused the
//     trigger so the modal doesn't reopen on remount.

const ConfidenceBadge: React.FC<{ value: number }> = ({ value }) => {
  const variant = value >= 0.8 ? "success" : value >= 0.5 ? "warning" : "secondary";
  return <Badge variant={variant}>{Math.round(value * 100)}%</Badge>;
};

type Props = {
  l1: string;
  l2: string;
  isOpen: boolean;
  onClose: () => void;
  // Bumps when a new upload completes — forces a fresh fetch even if
  // the modal was previously open/closed for the same l1/l2.
  triggerKey?: string;
};

export const DuplicateUploadModal: React.FC<Props> = ({
  l1,
  l2,
  isOpen,
  onClose,
  triggerKey,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { data: jobDocs } = useSelector((state: ReduxState) => state.JobDocument);
  const [data, setData] = useState<DuplicateBankTransactionsResponse | null>(null);
  const [loading, setLoading] = useState(false);

  // Fetch once per (l1, l2, triggerKey) when opened. We do NOT cache
  // across opens — duplicates are an upload-moment decision; if the
  // user reopens via a fresh upload, fetch again.
  useEffect(() => {
    if (!isOpen || !l1 || !l2) return;
    let cancelled = false;
    setLoading(true);
    setData(null);
    getDuplicateBankTransactionsForProject(l1, l2)
      .then((rsp) => {
        if (cancelled) return;
        setData(rsp);
        setLoading(false);
        // Self-close on empty result so the caller's URL-clear logic
        // can run via onClose without showing an empty modal.
        if (!rsp || (rsp.totalGroups || 0) === 0) {
          onClose();
        }
      })
      .catch(() => {
        if (cancelled) return;
        // Silent degradation — 403, 5xx, network errors all collapse
        // to "no modal at all". Same contract as the old chip.
        setLoading(false);
        onClose();
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, l1, l2, triggerKey]);

  const docName = useCallback(
    (id: any): string => {
      const d = (jobDocs || []).find(
        (x: any) => String(x.documentId) === String(id)
      ) as any;
      if (!d) return `Document ${id}`;
      return d.documentName || d.name || d.originalname || `Document ${id}`;
    },
    [jobDocs]
  );

  const handleReview = useCallback(
    (m: DuplicateBankTransactionMember) => {
      // Cross-job navigation: a project-scope group can span jobs,
      // so route to the member's job before highlighting. Mirrors the
      // old chip's "Jump to" behavior.
      if (m.documentId) {
        try {
          dispatch(setSelectedDocumentId(String(m.documentId)));
        } catch {
          /* ignore */
        }
      }
      if (m.jobId) {
        const target = `/app/extractdata?jobId=${encodeURIComponent(
          String(m.jobId)
        )}`;
        window.location.href = target;
        return;
      }
      onClose();
    },
    [dispatch, onClose]
  );

  const handleReviewInDateGaps = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.set("mode", "analyze");
    params.set("analyzeMode", "gaps");
    params.delete("newDocuments");
    history.push({ pathname: location.pathname, search: params.toString() });
    onClose();
  }, [history, location, onClose]);

  if (!isOpen) return null;
  // While loading, render nothing — we'd rather flash open after the
  // fetch resolves than show a spinner-only modal that may immediately
  // self-close on an empty response.
  if (loading || !data) return null;

  const groups: DuplicateBankTransactionGroup[] = data.groups || [];
  const totalDuplicateLines = data.totalDuplicateLines || 0;
  if (groups.length === 0) return null;

  const anyMemberHasJobId = groups.some((g) =>
    g.members.some((m) => !!m.jobId)
  );

  return (
    <Modal show={isOpen} onHide={onClose} size="lg">
      <Modal.Header
        closeButton
        placeholder=""
        onPointerEnterCapture={() => {}}
        onPointerLeaveCapture={() => {}}
      >
        <Modal.Title>Possible duplicate transactions detected</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted">
          We found {totalDuplicateLines} transaction
          {totalDuplicateLines === 1 ? "" : "s"} in your upload that look
          like duplicates of existing data in this project. Review and
          decide what to do — you can dismiss this for now and revisit it
          later from the Date Gaps tab.
        </p>
        {groups.map((g) => (
          <div
            key={g.key}
            className="mb-3 p-3"
            style={{ border: "1px solid #e1e4e8", borderRadius: 6 }}
          >
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <strong>{g.date}</strong>
                <span className="mx-2 text-muted">|</span>
                <span>{g.account}</span>
                <span className="mx-2 text-muted">|</span>
                <strong>${g.amount.toLocaleString()}</strong>
                {g.accountSoftened && (
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <BsTooltip id={`acct-soft-${g.key}`}>
                        Matched across different account labels — the
                        date/amount/description signature was strong
                        enough that the account-name mismatch was
                        treated as a labeling difference.
                      </BsTooltip>
                    }
                  >
                    <Badge
                      variant="info"
                      className="ml-2"
                      data-testid="account-softened-badge"
                    >
                      account bucket softened
                    </Badge>
                  </OverlayTrigger>
                )}
              </div>
              <div>
                <ConfidenceBadge value={g.confidence} />
              </div>
            </div>
            <div className="text-muted small mt-1">{g.reason}</div>
            <Table size="sm" className="mt-2 mb-0">
              <thead>
                <tr>
                  <th>Source document</th>
                  {anyMemberHasJobId && <th>Job</th>}
                  <th>Description</th>
                  <th style={{ width: 90 }}></th>
                </tr>
              </thead>
              <tbody>
                {g.members.map((m) => (
                  <tr key={m.transactionLineId}>
                    <td>{docName(m.documentId)}</td>
                    {anyMemberHasJobId && (
                      <td>
                        {m.jobId ? (
                          <small className="text-muted">#{m.jobId}</small>
                        ) : (
                          <small className="text-muted">—</small>
                        )}
                      </td>
                    )}
                    <td>{m.description}</td>
                    <td>
                      <Btn
                        variant="tertiary"
                        size="sm"
                        onClick={() => handleReview(m)}
                      >
                        Review
                      </Btn>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ))}
      </Modal.Body>
      <Modal.Footer>
        <Btn variant="tertiary" onClick={onClose}>
          Dismiss
        </Btn>
        <Btn variant="primary" onClick={handleReviewInDateGaps}>
          Review in Date Gaps
        </Btn>
      </Modal.Footer>
    </Modal>
  );
};

export default DuplicateUploadModal;
